/**
 * BannerPreview — an approximation of the RN `<AdminBanner>` render, used as the
 * live-preview panel in the editor. This is a design guide, not a pixel-perfect
 * emulator: colors/icons are a reasonable stand-in and can be reconciled against
 * the real component in a follow-up if a visual mismatch matters.
 */
import type { BannerSeverity } from '../../types';

const DEFAULT_CTA_LABEL = 'Learn more';

interface SeverityStyle {
  container: string;
  icon: string;
  iconColor: string;
  cta: string;
}

const SEVERITY_STYLES: Record<BannerSeverity, SeverityStyle> = {
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-900',
    icon: 'ℹ️',
    iconColor: 'text-blue-500',
    cta: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  warning: {
    container: 'bg-amber-50 border-amber-300 text-amber-900',
    icon: '⚠️',
    iconColor: 'text-amber-500',
    cta: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  urgent: {
    container: 'bg-red-50 border-red-300 text-red-900',
    icon: '⛔',
    iconColor: 'text-red-500',
    cta: 'bg-red-600 hover:bg-red-700 text-white',
  },
  promo: {
    container: 'bg-brand-50 border-brand-200 text-brand-900',
    icon: '✨',
    iconColor: 'text-brand-500',
    cta: 'bg-brand-600 hover:bg-brand-700 text-white',
  },
};

interface BannerPreviewProps {
  title: string;
  body: string;
  severity: BannerSeverity;
  dismissible: boolean;
  /** Non-empty ⇒ show a CTA button. */
  hasDeepLink: boolean;
  ctaLabel?: string | null;
}

export function BannerPreview({
  title,
  body,
  severity,
  dismissible,
  hasDeepLink,
  ctaLabel,
}: BannerPreviewProps) {
  const style = SEVERITY_STYLES[severity];
  const trimmedCta = (ctaLabel ?? '').trim();
  const resolvedCta = trimmedCta ? trimmedCta : DEFAULT_CTA_LABEL;

  return (
    <div className={`relative rounded-xl border px-4 py-3 shadow-sm ${style.container}`}>
      {dismissible && (
        <button
          type="button"
          aria-label="Dismiss (preview)"
          className="absolute top-2 right-2 text-lg leading-none opacity-50 hover:opacity-80"
          // Preview-only — no dismiss behavior.
          onClick={(e) => e.preventDefault()}
        >
          ×
        </button>
      )}
      <div className="flex gap-3">
        <span className={`text-xl leading-none ${style.iconColor}`}>{style.icon}</span>
        <div className={`flex-1 ${dismissible ? 'pr-5' : ''}`}>
          <p className="font-semibold text-sm">
            {title.trim() ? title : <span className="opacity-40">Banner title</span>}
          </p>
          <p className="text-sm mt-0.5 whitespace-pre-wrap opacity-90">
            {body.trim() ? body : <span className="opacity-40">Banner body text…</span>}
          </p>
          {hasDeepLink && (
            <button
              type="button"
              className={`mt-2.5 inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${style.cta}`}
              onClick={(e) => e.preventDefault()}
            >
              {resolvedCta}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
